import React, { useEffect, useRef, useMemo } from "react";
import { connect } from "react-redux";
import { Checkbox, Icon, Timeline, message, Divider } from "antd";
import { useTranslation } from "react-i18next";
import copy from "copy-to-clipboard";

import * as gtag from "../../../lib/gtag";
import {
  generateEmailOpportunity,
  GENERATE_EMAIL_OPPORTUNITY
} from "../../../store/actions/opportunity";
import { isLoading } from "../../../utils/actions";
import Button from "../../../components/Button";

import JobEmailModal from "./JobEmailModal";

import styled from "styled-components/macro";

const StyledTimeline = styled(Timeline)`
  margin-top: 32px !important;
  margin-left: 8px !important;

  .anticon {
    font-size: 24px;
  }

  .generateButton .anticon {
    font-size: 14px;
  }

  .ant-timeline-item {
    margin-bottom: 12px;
  }

  .ant-timeline-item:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
  }

  .ant-timeline-item-content {
    margin-left: 32px !important;
    display: flex;
    flex-direction: column;
  }

  .subtitle {
    margin-top: 2px;
  }

  .title {
    font-weight: 600;
  }
`;

const WrapperEmail = styled.div`
  display: flex;
`;

const WrapperTitle = styled.div`
  display: grid;
`;

const JobEmailForm = ({
  opportunity,
  createJobPostEmail,
  setCreateJobPostEmail,
  generatingEmail,
  handleGenerateEmail,
  section,
  isDisabled
}) => {
  const { t } = useTranslation();
  const sectionName = useMemo(
    () => t("opportunity:publishing.jobPostEmail.sectionName"),
    [t]
  );
  const ref = useRef(null);
  useEffect(() => {
    if (section && section === sectionName && ref.current) {
      ref.current.scrollIntoView();
    }
  }, [section, sectionName]);
  const [showJobEmailModal, setShowJobEmailModal] = React.useState(false);

  return (
    <div ref={ref}>
      {!opportunity && <Divider />}
      <h2>
        {t("opportunity:publishing.jobPostEmail.title")}
        <Icon
          style={{ marginLeft: 10, color: "#3D64FF" }}
          type="question-circle"
          onClick={() => setShowJobEmailModal(true)}
        />
      </h2>
      {opportunity ? (
        <StyledTimeline>
          <Timeline.Item dot={<Icon type="mail" theme="outlined" />}>
            <WrapperEmail data-test="div-jobPost-email">
              <WrapperTitle>
                <span className="title">
                  {opportunity.emailError
                    ? t("opportunity:publishing.jobPostEmail.errorMailTitle")
                    : opportunity.jobPostEmail
                    ? t("opportunity:publishing.jobPostEmail.title1")
                    : t("opportunity:publishing.jobPostEmail.title2")}
                </span>
                <span className="subtitle">
                  {opportunity.jobPostEmail ? (
                    <>
                      <span
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          copy(opportunity.jobPostEmail.toLowerCase());
                          message.success(
                            t("opportunity:publishing.jobPostEmail.message")
                          );
                        }}
                      >
                        {opportunity.jobPostEmail.toLowerCase()}
                      </span>
                      <Button
                        size="sm"
                        style={{
                          marginLeft: 10
                        }}
                        data-test="copy-email-button"
                        onClick={() => {
                          gtag.event({
                            category: "VacancyDetail",
                            action: "VacancyDetail.Email-Copy",
                            label: opportunity.id
                          });
                          copy(opportunity.jobPostEmail.toLowerCase());
                          message.success(
                            t("opportunity:publishing.jobPostEmail.message")
                          );
                        }}
                      >
                        {t("opportunity:publishing.jobPostEmail.copy")}
                      </Button>
                    </>
                  ) : (
                    <>
                      {opportunity.emailError
                        ? t(
                            "opportunity:publishing.jobPostEmail.errorMailSubtitle"
                          )
                        : t("opportunity:publishing.jobPostEmail.subtitle")}
                    </>
                  )}
                </span>
              </WrapperTitle>
              <div style={{ margin: "0 auto", marginLeft: 20 }}>
                {(opportunity.emailError || !opportunity.jobPostEmail) && (
                  <Button
                    size="sm"
                    className="generateButton"
                    data-test="generated-email-button"
                    loading={generatingEmail}
                    disabled={isDisabled}
                    onClick={() => {
                      gtag.event({
                        category: "VacancyDetail",
                        action: "VacancyDetail.Email-Add",
                        label: opportunity.id
                      });
                      handleGenerateEmail(opportunity.id);
                    }}
                  >
                    {t("opportunity:publishing.jobPostEmail.generate")}
                  </Button>
                )}
              </div>
            </WrapperEmail>
          </Timeline.Item>
        </StyledTimeline>
      ) : (
        <Checkbox
          defaultChecked={createJobPostEmail}
          onChange={e => setCreateJobPostEmail(e.target.checked)}
        >
          {t("opportunity:publishing.jobPostEmail.sharedEmail")}
        </Checkbox>
      )}
      {showJobEmailModal && (
        <JobEmailModal onClose={() => setShowJobEmailModal(false)} />
      )}
    </div>
  );
};

export default connect(
  state => ({
    generatingEmail: isLoading(GENERATE_EMAIL_OPPORTUNITY, state)
  }),
  {
    handleGenerateEmail: generateEmailOpportunity
  }
)(JobEmailForm);
